import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ListFilterIcon,
  CheckIcon,
} from 'lucide-react';

/**
 * The old grid's bottom bar (`AgGridBottom`), shared by every legacy table:
 * the "Показано X - Y из N записей" counter, a NUMBERED pager (sibling 1,
 * boundary 1, MUI-style ellipsis) and the "Показать по N" page-size dropdown
 * with the old option set [5, 15, 25, 35, 45, 55].
 *
 * The old bar rendered whenever the table had rows — even on a single page —
 * so callers should gate on `totalCount > 0`, not on `pageCount > 1`.
 */
export const LEGACY_PAGE_SIZE_OPTIONS = [5, 15, 25, 35, 45, 55];

export function LegacyTablePager({
  page,
  pageCount,
  totalCount,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = LEGACY_PAGE_SIZE_OPTIONS,
  withDivider = true,
}: {
  /** Current page, 1-based. */
  page: number;
  pageCount: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  /** Omit to hide the page-size dropdown (a table with a fixed size). */
  onPageSizeChange?: ((size: number) => void) | undefined;
  pageSizeOptions?: number[];
  /**
   * The top divider over the bar. The reports footer sits straight on the
   * page grey with no rule above it (see its reference); every other grid
   * keeps the divider.
   */
  withDivider?: boolean;
}) {
  const { t } = useTranslation('legacy');
  const [sizeOpen, setSizeOpen] = useState(false);

  /**
   * The counter reads from the rows this page can actually hold, so it stays
   * truthful even when `page` is briefly out of range — a stale `?page=9` on a
   * 6-page list rendered "Показано 121 - 86 из 86 записей" before this clamp.
   * The caller snaps the page back; this keeps the label sane in the meantime.
   */
  const start = Math.min((page - 1) * pageSize, Math.max(0, totalCount - 1));
  const end = Math.min(start + pageSize, totalCount);
  const items = paginationItems(page, Math.max(pageCount, 1));

  return (
    <div
      className={[
        'flex w-full flex-col items-center justify-between gap-2 py-2.5 sm:flex-row',
        withDivider ? 'border-t border-[var(--lg-grey-100,var(--border,#e5e7eb))]' : '',
      ].join(' ')}
    >
      <p className="text-[14px] leading-[13.9px] font-medium text-[var(--lg-grey-400,var(--muted-foreground,#9ca3af))]">
        {t('table.seen')} {start + 1} - {end} {t('table.from')} {totalCount}{' '}
        {t('table.entries')}
      </p>

      <nav className="flex items-center" aria-label="Pagination">
        <button
          type="button"
          aria-label="Предыдущая страница"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          className="mr-1 flex h-10 cursor-pointer items-center justify-center bg-transparent px-1 text-[var(--lg-fg,var(--foreground,#111827))] disabled:cursor-default disabled:opacity-40"
        >
          <ChevronLeftIcon className="size-4" />
        </button>
        {items.map((item, i) =>
          item === 'ellipsis' ? (
            <span
              key={`e-${i}`}
              className="flex h-10 min-w-10 items-center justify-center text-[var(--lg-grey-600,var(--muted-foreground,#4b5563))]"
            >
              …
            </span>
          ) : (
            <button
              key={item}
              type="button"
              aria-label={`Страница ${item}`}
              aria-current={item === page ? 'page' : undefined}
              onClick={() => onPageChange(item)}
              className={`flex h-10 min-w-10 cursor-pointer items-center justify-center rounded-[16px] border p-1 text-[16px] leading-[19px] font-semibold hover:bg-[var(--lg-grey-100,var(--muted,#f3f4f6))] ${
                item === page
                  ? 'border-[var(--lg-grey-200,var(--border,#e5e7eb))] text-[var(--lg-orange-600,var(--primary,#ea580c))] hover:bg-transparent'
                  : 'border-transparent text-[var(--lg-grey-600,var(--muted-foreground,#4b5563))]'
              }`}
            >
              {item}
            </button>
          ),
        )}
        <button
          type="button"
          aria-label="Следующая страница"
          disabled={page >= pageCount}
          onClick={() => onPageChange(page + 1)}
          className="ml-1 flex h-10 cursor-pointer items-center justify-center bg-transparent px-1 text-[var(--lg-fg,var(--foreground,#111827))] disabled:cursor-default disabled:opacity-40"
        >
          <ChevronRightIcon className="size-4" />
        </button>
      </nav>

      {onPageSizeChange ? (
        <div className="relative min-w-[196px]">
          <button
            type="button"
            onClick={() => setSizeOpen((open) => !open)}
            className="flex h-10 w-full cursor-pointer items-center justify-center gap-2 rounded-[96px] border border-[var(--lg-grey-200,var(--border,#e5e7eb))] bg-[var(--lg-surface,var(--card,#fff))] px-4 text-[14px] font-semibold text-[var(--lg-fg,var(--foreground,#111827))] hover:bg-[var(--lg-grey-50,var(--muted,#f9fafb))]"
          >
            <ListFilterIcon className="size-4" />
            {t('table.see_by')} {pageSize}
          </button>
          {sizeOpen && (
            <>
              {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- invisible click-away layer; the list below stays keyboard-operable */}
              <div className="fixed inset-0 z-[999]" onClick={() => setSizeOpen(false)} />
              <ul className="absolute bottom-12 left-0 z-[1000] w-full overflow-hidden rounded-[16px] border border-[var(--lg-grey-200,var(--border,#e5e7eb))] bg-[var(--lg-surface,var(--popover,#fff))] py-1 shadow-lg">
                {pageSizeOptions.map((opt) => (
                  <li key={opt}>
                    <button
                      type="button"
                      onClick={() => {
                        onPageSizeChange(opt);
                        setSizeOpen(false);
                      }}
                      className="flex w-full cursor-pointer items-center justify-between px-4 py-2 text-[14px] text-[var(--lg-fg,var(--foreground,#111827))] hover:bg-[var(--lg-grey-100,var(--muted,#f3f4f6))]"
                    >
                      <span>
                        {opt} {t('table.rows')}
                      </span>
                      {opt === pageSize && (
                        <CheckIcon className="size-4 text-[var(--lg-orange-500,var(--primary,#ff6d00))]" />
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      ) : (
        // Keep the three-column balance so the pager stays centred.
        <div className="hidden min-w-[196px] sm:block" />
      )}
    </div>
  );
}

/**
 * MUI's `Pagination` windowing with `siblingCount=1`, `boundaryCount=1`:
 * first page, an ellipsis when the gap is real, the current page ±1, an
 * ellipsis, last page.
 */
function paginationItems(page: number, pageCount: number): (number | 'ellipsis')[] {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, i) => i + 1);
  }
  const items: (number | 'ellipsis')[] = [1];
  const from = Math.max(2, page - 1);
  const to = Math.min(pageCount - 1, page + 1);
  if (from > 2) items.push('ellipsis');
  for (let i = from; i <= to; i++) items.push(i);
  if (to < pageCount - 1) items.push('ellipsis');
  items.push(pageCount);
  return items;
}
